import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import Autocomplete from "react-autocomplete";

import { Ingredient } from "../../../../models/product_model";
import { SAVE_INGREDIENT, SEARCH_INGREDIENTS } from "../queries";
import { ActionType } from "./productContext/actions";
import { ProductContext } from "./productContext/context";
import { InputIngredient } from "./productContext/state";
import { lang } from "../../../../middleware/lang";

const menuStyle = {
  borderRadius: "3px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
  background: "rgba(255, 255, 255, 1)",
  padding: "2px 0",
  fontSize: "90%",
  position: "fixed",
  overflow: "auto",
  maxHeight: "50%",
  zIndex: 5000,
};

interface Props {
  groupIdx: number;
}

const GroupOptionsSearch: React.FC<Props> = ({ groupIdx }) => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Ingredient[]>([]);
  const [searchIngredients, { data }] = useLazyQuery(SEARCH_INGREDIENTS, {
    fetchPolicy: "no-cache",
  });
  const [saveNewIngredient, { data: new_ing }] = useMutation<{
    saveIngredient: InputIngredient;
  }>(SAVE_INGREDIENT);
  const { dispatch, state } = useContext(ProductContext);

  const currentGroup = state.option_groups[groupIdx];

  useEffect(() => {
    if (data) {
      const existingIds = currentGroup?.options.map((o) => o.ingredient.id) || [];
      const filtered = data.searchIngredients.filter(
        (i: InputIngredient) => !existingIds.includes(i.id)
      );
      if (filtered.length > 0) {
        setSearchResults(filtered);
      } else if (search.length > 2) {
        setSearchResults([{ name: search, name_en: search, id: "0" }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentGroup?.options]);

  useEffect(() => {
    if (new_ing) {
      addOption(new_ing.saveIngredient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [new_ing]);

  const set = (text: string) => {
    setSearch(text);
    getIngredients();
  };

  const addOption = (ingredient: InputIngredient) => {
    if (!ingredient.id || ingredient.id === "0") {
      saveNewIngredient({
        variables: { ingredient: ingredient.name, lang: "en" },
      });
      return;
    }
    dispatch({
      type: ActionType.AddOptionToGroup,
      payload: {
        groupIdx,
        option: { ingredient, price_mod: 0 },
      },
    });
    setSearch("");
  };

  const getIngredients = useDebouncedCallback(async () => {
    if (search && search.length > 1) {
      searchIngredients({ variables: { search } });
    }
  }, 400);

  return (
    <div className="form-group p_relative">
      <Autocomplete
        getItemValue={(item: Ingredient) => item.name}
        //@ts-ignore
        menuStyle={menuStyle}
        items={searchResults}
        value={search}
        onChange={(_, value) => set(value)}
        onSelect={(_, ingredient: Ingredient) => addOption(ingredient)}
        renderInput={(props) => (
          <input
            type="text"
            className="form-control form-control-sm dropdown-toggle text-capitalize"
            placeholder={lang("Buscar opciones para este grupo", "Search options for this group")}
            {...props}
          />
        )}
        renderItem={(ingredient: Ingredient) => (
          <div
            key={`ingredient-${ingredient.id}`}
            className="dropdown-item py-2 text-capitalize"
          >
            <i className="fa fa-plus text-primary" aria-hidden="true"></i>{" "}
            {ingredient.name}
          </div>
        )}
      />
    </div>
  );
};

export default GroupOptionsSearch;
